import { useState, useCallback, useRef, useEffect } from "react";
import { X, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { normalizeSmartSearchText, smartTextMatches } from "@shared/search/smartSearch";

interface RoleAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const DEFAULT_ROLES = [
  "CEO",
  "Founder",
  "Co-Founder",
  "Director",
  "Manager",
  "Sales Manager",
  "Marketing Manager",
  "Product Manager",
  "Project Manager",
  "Operations Manager",
  "Business Development",
  "Account Manager",
  "Key Account Manager",
  "Sales Representative",
  "Developer",
  "Senior Developer",
  "Lead Developer",
  "CTO",
  "CFO",
  "COO",
  "HR Manager",
  "Recruiter",
  "Consultant",
  "Advisor",
  "Investor",
  "Partner",
  "Owner",
  "Franchisee",
  "Master Franchisee",
  "Area Developer",
  "Real Estate Manager",
  "Property Manager",
  "Leasing Manager",
  "Architect",
  "Designer",
  "Engineer",
  "Procurement",
  "Supply Chain",
  "Logistics",
  "Legal Counsel",
  "Administrative",
  "Assistant",
  "Receptionist",
  "Other",
];

export function RoleAutocomplete({
  value,
  onChange,
  placeholder = "Cerca o crea ruolo...",
  disabled = false,
  className = "",
}: RoleAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredRoles, setFilteredRoles] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    if (inputValue.trim()) {
      const q = normalizeSmartSearchText(inputValue);
      const matches = DEFAULT_ROLES.filter((role) => smartTextMatches(role, q));
      // Se l'input non matcha esattamente, aggiungi opzione "Crea: ..."
      const exactMatch = matches.some((role) => normalizeSmartSearchText(role) === q);
      if (!exactMatch) {
        setFilteredRoles([...matches, `__CREATE__${inputValue.trim()}`]);
      } else {
        setFilteredRoles(matches);
      }
    } else {
      setFilteredRoles([]);
    }
  }, [inputValue]);

  // Chiudi dropdown al click fuori
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (role: string) => {
      if (role.startsWith("__CREATE__")) {
        const newRole = role.replace("__CREATE__", "");
        onChange(newRole);
        setInputValue(newRole);
      } else {
        onChange(role);
        setInputValue(role);
      }
      setIsOpen(false);
    },
    [onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      onChange(inputValue.trim());
      setIsOpen(false);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const clearValue = () => {
    onChange("");
    setInputValue("");
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center gap-1">
        <Input
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1"
        />
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearValue}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isOpen && filteredRoles.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border shadow-lg max-h-60 overflow-y-auto">
          {filteredRoles.map((role, idx) => {
            if (role.startsWith("__CREATE__")) {
              const newRole = role.replace("__CREATE__", "");
              return (
                <button
                  key="create"
                  type="button"
                  onClick={() => handleSelect(role)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 text-primary"
                >
                  <Plus className="h-3 w-3" />
                  Crea "{newRole}"
                </button>
              );
            }
            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleSelect(role)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
              >
                {role}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
